import React, { useEffect } from 'react';
import {
  Briefcase,
  Brain,
  Network,
  Compass,
  ArrowRight,
  Calendar,
  Clock,
  Users,
  Target
} from 'lucide-react';
import Header from '../components/Header';
import Footer from '../components/Footer';
import Seo from '../components/Seo';

const FractionalAdvisoryPage = () => {
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  return (
    <div className="min-h-screen bg-white">
    <Seo title="Fractional CTO & Solutions Architecture Advisory" description="Fractional technical leadership and integration architecture advisory for logistics and eCommerce startups — enterprise-grade guidance without the enterprise headcount." path="/services/fractional" />
      <Header />

      {/* Hero / Intro */}
      <section className="pt-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h1 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
              Fractional CTO &amp; Technology Leadership — Without the Full-Time Overhead
            </h1>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Plug in enterprise-grade architecture, integration, and AI expertise exactly when
              you need it. Flexible engagements designed to give you senior technology leadership
            matched to your stage, your stack, and your strategy.
            </p>
          </div>
        </div>
      </section>

      {/* Advisory Roles Grid */}
      <section className="pb-8 px-4 bg-white text-black">
        <div className="max-w-6xl mx-auto text-center">
          <div className="bg-black text-white rounded-xl grid grid-cols-1 md:grid-cols-2 text-left">
            {[
              {
                icon: <Briefcase size={24} />,
                title: "Fractional CTO",
                desc: "Part-time executive technology leadership for startups and SMBs that need a strategic technologist in the seat — but not a full-time salary. We own the technical vision, hiring plan, and architecture decisions alongside your leadership team.",
                features: [
                  "Technology strategy & roadmapping",
                  "Engineering hiring & team building",
                  "Vendor, build-vs-buy, & platform decisions",
                  "Investor & board-level technical communication",
                ],
              },
              {
                icon: <Compass size={24} />,
                title: "Fractional Solutions Architect",
                desc: "Embedded architectural leadership for teams shipping complex, cross-system work. We partner with your engineers to design resilient, scalable systems — from greenfield platforms to enterprise-scale integrations across logistics, eCommerce, and supply chain.",
                features: [
                  "System & integration architecture design",
                  "Technical discovery & solution shaping",
                  "Architecture review & technical due diligence",
                  "Mentorship for in-house engineering leads",
                ],
              },
              {
                icon: <Brain size={24} />,
                title: "AI & Agentic Strategy Advisor",
                desc: "Cut through AI hype and land on what actually moves your business. We help you identify high-leverage AI use cases, design agentic workflows, evaluate build-vs-buy tradeoffs, and stand up production-grade systems on whichever platform fits your stack.",
                features: [
                  "AI opportunity assessment & prioritization",
                  "Agentic workflow & architecture design",
                  "LLM & platform selection",
                  "Responsible AI, security, & governance",
                ],
              },
              {
                icon: <Network size={24} />,
                title: "Integration & iPaaS Advisor",
                desc: "Strategic guidance on B2B integration, EDI, and iPaaS platform investments. We bring deep, platform-independent integration expertise to help you standardize integration patterns, reduce partner onboarding time, and build a scalable integration center of excellence.",
                features: [
                  "iPaaS platform selection & strategy",
                  "EDI & trading partner program design",
                  "Integration CoE & governance models",
                  "Supply chain & logistics systems strategy",
                ],
              },
            ].map((role, i) => (
              <div
                key={i}
                className="bg-white/5 p-8 hover:bg-white/10 transition-all duration-300 group"
              >
                <div className="w-12 h-12 bg-black text-white flex items-center justify-center mb-6 transition-transform group-hover:scale-110">
                  {role.icon}
                </div>
                <h3 className="text-xl font-bold mb-3">{role.title}</h3>
                <p className="text-gray-400 leading-relaxed mb-6">
                  {role.desc}
                </p>
                <ul className="space-y-2">
                  {role.features.map((f, j) => (
                    <li
                      key={j}
                      className="text-sm text-gray-300 flex items-start"
                    >
                      <span className="mr-2 mt-1 w-1 h-1 bg-white rounded-full flex-shrink-0"></span>
                      {f}
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Quote / Philosophy break */}
      <section class="py-20 bg-black">
        <div class="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                <p class="text-center mb-16 text-3xl italic text-gray-100 max-w-3xl mx-auto">
            Great architecture isn't about the technology. It's about the business outcome on the other side of it.
                </p>
          </div>
    </section>

      {/* Engagement Models */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
              Engagement Models
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Flexible structures designed to match where you are — from a single architecture
              review to ongoing executive leadership.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            <div className="bg-white border border-gray-200 rounded-xl p-8 hover:shadow-xl transition-all duration-300 hover:-translate-y-1">
              <div className="w-14 h-14 bg-white rounded-lg flex items-center justify-center mb-6">
                <Calendar className="w-8 h-8 text-gray-600" />
              </div>
              <h3 className="text-2xl font-bold text-gray-900 mb-4">Monthly Retainer</h3>
              <p className="text-gray-600 mb-6 leading-relaxed">
                Ongoing fractional engagement with a committed cadence of strategy sessions,
                architecture reviews, and team support. Ideal for founders and leadership teams
                who need a consistent senior technologist in the loop.
              </p>
              <ul className="space-y-2 text-sm text-gray-600">
                <li>• Predictable monthly commitment</li>
                <li>• Embedded in your team's rhythm</li>
                <li>• Strategic continuity over time</li>
              </ul>
            </div>

            <div className="bg-white border border-gray-200 rounded-xl p-8 hover:shadow-xl transition-all duration-300 hover:-translate-y-1">
              <div className="w-14 h-14 bg-white rounded-lg flex items-center justify-center mb-6">
                <Target className="w-8 h-8 text-gray-600" />
              </div>
              <h3 className="text-2xl font-bold text-gray-900 mb-4">Project-Based Advisory</h3>
              <p className="text-gray-600 mb-6 leading-relaxed">
                Scoped, outcome-driven engagements with a clear deliverable — an architecture
                blueprint, AI roadmap, integration strategy, or technical due diligence report.
                Defined start, defined finish.
              </p>
              <ul className="space-y-2 text-sm text-gray-600">
                <li>• Fixed scope & timeline</li>
                <li>• Clear, documented deliverables</li>
                <li>• Ideal for discrete initiatives</li>
              </ul>
            </div>

            <div className="bg-white border border-gray-200 rounded-xl p-8 hover:shadow-xl transition-all duration-300 hover:-translate-y-1">
              <div className="w-14 h-14 bg-white rounded-lg flex items-center justify-center mb-6">
                <Clock className="w-8 h-8 text-gray-600" />
              </div>
              <h3 className="text-2xl font-bold text-gray-900 mb-4">Hourly Consulting</h3>
              <p className="text-gray-600 mb-6 leading-relaxed">
                On-demand expertise for focused problems — a design review, a vendor evaluation,
                a stuck integration, or a second opinion on a critical technical decision. Pay
                for exactly what you use.
              </p>
              <ul className="space-y-2 text-sm text-gray-600">
                <li>• No long-term commitment</li>
                <li>• Fast turnaround</li>
                <li>• Best for targeted questions</li>
              </ul>
            </div>

            <div className="bg-white border border-gray-200 rounded-xl p-8 hover:shadow-xl transition-all duration-300 hover:-translate-y-1">
              <div className="w-14 h-14 bg-white rounded-lg flex items-center justify-center mb-6">
                <Users className="w-8 h-8 text-gray-600" />
              </div>
              <h3 className="text-2xl font-bold text-gray-900 mb-4">Board & Equity Advisor</h3>
              <p className="text-gray-600 mb-6 leading-relaxed">
                Long-term advisory seats for early-stage companies where deep technical judgment
                and logistics/AI domain expertise are strategic differentiators. Cash, equity,
                or hybrid compensation considered.
              </p>
              <ul className="space-y-2 text-sm text-gray-600">
                <li>• Strategic, long-horizon relationship</li>
                <li>• Networking & investor support</li>
                <li>• Typically 1–2 hours / month</li>
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* Who It's For */}
      <section className="py-20 px-4 bg-gray-50">
        <div className="max-w-6xl mx-auto">
          <div className="max-w-6xl mx-auto">
            <h2 className="text-4xl font-black mb-6">
              Built for teams that need senior horsepower — not another full-time hire.
            </h2>
            <p className="text-xl text-gray-600 leading-relaxed mb-8">
              Our advisory engagements are designed for companies at an inflection point, where
              the next technology decision materially affects growth. We bring the pattern
              recognition of enterprise-scale systems to teams that need to move faster than
              they can hire.
            </p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8 mt-12">
            {[
              {
                title: "Early-Stage Startups",
                desc: "Pre-Series A to Series B founders who need a credible technical voice for investors, a roadmap they can defend, and architecture decisions that won't need to be re-done in 18 months.",
              },
              {
                title: "SMB Logistics & eCommerce",
                desc: "Operators building on iPaaS, 3PLs, and integration-heavy stacks who need the same rigor enterprises enjoy — without a six-figure headcount investment.",
              },
              {
                title: "Mid-Market Leaders",
                desc: "Companies scaling integrations, standing up AI programs, or modernizing supply chain systems who need an experienced architect to shape the strategy alongside internal teams.",
              },
            ].map((item, i) => (
              <div
                key={i}
                className="bg-white p-8 shadow-sm hover:shadow-lg transition-all duration-300 transform hover:scale-105"
              >
                <h3 className="text-xl font-bold text-gray-900 mb-3">{item.title}</h3>
                <p className="text-gray-600 leading-relaxed">{item.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Impact / Stats */}
      <section className="py-20 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
              The Fractional Advantage
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Companies are increasingly turning to fractional leadership to access senior expertise
              with the flexibility modern businesses require.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            <div className="bg-white rounded-xl p-10 text-center shadow-sm border border-gray-200 hover:shadow-lg transition-all duration-300">
              <div className="text-5xl md:text-6xl font-black text-indigo-600 mb-4">40–70%</div>
              <h3 className="text-xl font-bold text-gray-900 mb-3">Lower Cost vs. Full-Time Hire</h3>
              <p className="text-gray-600 leading-relaxed">
                Fractional executives typically deliver strategic value at a fraction of
                the loaded cost of a full-time senior leader — no equity dilution, no benefits.
              </p>
              <p className="text-xs text-gray-400 mt-4">
                Source: Based on typical Tare Labs engagement ramp
              </p>
            </div>

            <div className="bg-white rounded-xl p-10 text-center shadow-sm border border-gray-200 hover:shadow-lg transition-all duration-300">
              <div className="text-5xl md:text-6xl font-black text-indigo-600 mb-4">13+</div>
              <h3 className="text-xl font-bold text-gray-900 mb-3">Years of Enterprise Experience</h3>
              <p className="text-gray-600 leading-relaxed">
                From Amazon and Flexport to UPS — real experience building the integrations,
                supply chain systems, and AI that global logistics runs on, now available to
                companies of any size.
              </p>
              <p className="text-xs text-gray-400 mt-4">
                Source: Tare Labs — Founder Profile
              </p>
            </div>

            <div className="bg-white rounded-xl p-10 text-center shadow-sm border border-gray-200 hover:shadow-lg transition-all duration-300">
              <div className="text-5xl md:text-6xl font-black text-indigo-600 mb-4">2–4 wks</div>
              <h3 className="text-xl font-bold text-gray-900 mb-3">Typical Time to Impact</h3>
              <p className="text-gray-600 leading-relaxed">
                No six-month executive search. Fractional engagements start delivering strategic
                output within the first month — architecture reviews, roadmaps, and actionable plans.
              </p>
              <p className="text-xs text-gray-400 mt-4">
                Source: Based on typical Tare Labs engagement ramp
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-20 px-6 bg-gray-100">
        <div className="container mx-auto">
          <div className="max-w-4xl mx-auto text-center">
            <h2 className="text-4xl md:text-5xl font-black mb-8">
              Let's talk about what your team actually needs.
            </h2>
            <p className="text-xl text-gray-900 mb-12 leading-relaxed">
              Whether you're weighing an AI investment, rethinking your integration strategy,
              or looking for a technical partner who's been in the seat before — book a free
              discovery call and let's figure out the right fit.
            </p>

            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <a
                href="https://calendly.com/dan-tarelabs/new-meeting"
                className="bg-black hover:bg-gray-800 text-white px-10 py-5 font-bold text-lg transition-all duration-300 transform hover:scale-105 shadow-lg hover:shadow-xl inline-flex items-center justify-center group"
              >
                Get a Second Opinion
                <ArrowRight className="ml-2 w-5 h-5 group-hover:translate-x-1 transition-transform" />
              </a>
              <a
                href="mailto:dan@tarelabs.com"
                className="border-2 border-black text-black px-10 py-5 font-bold text-lg transition-all duration-300 hover:bg-black hover:text-white inline-flex items-center justify-center"
              >
                Email Us
              </a>
            </div>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default FractionalAdvisoryPage;
